/**
 * Client for the M8 question-pack control-plane endpoints (`/api/packs`, and the
 * pack-aware `POST /api/games`).
 *
 * A host uploads a pack (getting field-level validation errors back on a bad one —
 * FR-8.2), lists their own packs (owner-scoped — FR-8.4), and creates a game from a
 * chosen pack (snapshotted immutably at creation — FR-8.4). Identity is proven by
 * the signed guest token (FR-9.3), obtained via {@link ensureGuestIdentity}, never a
 * raw client id.
 *
 * These endpoints only exist on the deployed control plane; the local dev server
 * (M2) has no pack store, so the pack lobby is a prod-mode-only surface.
 */

import type { BoardSource, HostMode, ValidationError } from '@jeopardy/shared';
import { SERVER_HTTP_URL } from '../config';
import { ensureGuestIdentity } from './auth';
import { getFunnelKey } from './funnel-key';

// The bounded, resolved House Rules DTO lives in `@jeopardy/shared` (#328 promoted it out
// of this file so a saved-preset payload shares one type). Imported for in-file use and
// re-exported so the long-standing `../net/pack-api` `HouseRules` import site is unchanged.
import type { HouseRules } from '@jeopardy/shared';
export type { HouseRules };

/** Owner-scoped metadata for one uploaded pack (mirrors the server `PackRecord`). */
export interface PackMeta {
  packId: string;
  ownerId: string;
  name: string;
  version: string;
  sizeBytes: number;
  createdAtEpochMs: number;
}

/** A pack rejected by the validator: the field-level errors to surface (FR-8.2). */
export class PackValidationError extends Error {
  constructor(readonly errors: ValidationError[]) {
    super('Pack is invalid');
    this.name = 'PackValidationError';
  }
}

/**
 * Upload a pack document. Returns its stored metadata on success. Throws a
 * {@link PackValidationError} (carrying the field-level errors) on a 422, or a
 * plain Error on size/permission/server failures.
 */
export async function uploadPack(pack: unknown): Promise<PackMeta> {
  const guest = await ensureGuestIdentity();
  const res = await fetch(`${SERVER_HTTP_URL}/api/packs`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ pack, guestToken: guest.guestToken }),
  });
  if (res.status === 422) {
    const body = (await res.json().catch(() => ({}))) as { errors?: ValidationError[] };
    throw new PackValidationError(body.errors ?? []);
  }
  if (res.status === 413) {
    throw new Error('Pack is too large — reduce the file size and try again.');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Upload failed (${res.status})`);
  }
  return (await res.json()) as PackMeta;
}

/** List the caller's uploaded packs, newest first (owner-scoped, FR-8.4). */
export async function listPacks(): Promise<PackMeta[]> {
  const guest = await ensureGuestIdentity();
  // The signed guest token goes in a HEADER, not the query string, so this bearer
  // credential never lands in access logs / browser history / Referer.
  const res = await fetch(`${SERVER_HTTP_URL}/api/packs`, {
    headers: { 'x-guest-token': guest.guestToken },
  });
  if (!res.ok) throw new Error(`Could not list packs (${res.status})`);
  const body = (await res.json()) as { packs: PackMeta[] };
  return body.packs;
}

/**
 * Create a new game, optionally from one of the caller's uploaded packs (M8.2). The
 * server snapshots the pack into the game at creation, so a later edit to the pack
 * never alters this game (FR-8.4). Returns the new game's id + join code + mode.
 *
 * M12: passing `mode: 'DYNAMIC'` creates a pack-less game born in `LOBBY`; the board
 * is generated on the fly when the host starts generation. Omitting `mode` (or
 * `'PACK'`) is the pre-M12 uploaded/BYO-pack path, so existing callers are unchanged.
 *
 * #91: passing `hostMode: 'AUTO'` creates a hostless voice game (the in-actor
 * auto-host reads clues, opens buzzers, and LLM-judges spoken answers). Omitting it
 * (or `'HUMAN'`) is the human-hosted default, so existing callers are unchanged.
 *
 * #232: passing a `houseRules` object opts the game into power-ups (Game Night),
 * buzzer-smash lockout (unifying #198), and/or the negative-scores flag. Omitting it
 * ⇒ Classic ⇒ existing callers are unchanged. The server validates + maps it.
 */
export async function createGame(
  packId?: string,
  mode: BoardSource = 'PACK',
  hostMode: HostMode = 'HUMAN',
  houseRules?: HouseRules,
): Promise<{
  gameId: string;
  joinCode: string;
  mode: BoardSource;
}> {
  const guest = await ensureGuestIdentity();
  const res = await fetch(`${SERVER_HTTP_URL}/api/games`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      // A dynamic game has no pack; only send packId for the pack path.
      ...(mode === 'PACK' && packId ? { packId } : {}),
      ...(mode !== 'PACK' ? { mode } : {}),
      // Default HUMAN is the server default too; only send AUTO to keep the body
      // (and every existing caller/test) unchanged for human-hosted games.
      ...(hostMode !== 'HUMAN' ? { hostMode } : {}),
      // Only send houseRules when non-Classic, so a default create body (and every
      // existing caller/test) is unchanged (#232).
      ...(houseRules ? { houseRules } : {}),
      guestToken: guest.guestToken,
      // Anonymous funnel key (#148 D148.4) so the server's `game_created` log line joins
      // this conversion to the pre-signup funnel head.
      fkey: getFunnelKey(),
    }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Could not create game (${res.status})`);
  }
  const body = (await res.json()) as {
    gameId: string;
    joinCode: string;
    mode?: BoardSource;
  };
  // Older servers (pre-M12) don't echo mode; default to what we requested.
  return { gameId: body.gameId, joinCode: body.joinCode, mode: body.mode ?? mode };
}

/**
 * Mint a host ticket for a SPECIFIC game (the one the host just created). Carries the
 * caller's signed guest token so the server can prove they are the game's creator
 * (M11.5.1 host-ticket auth): host issuance is gated on `playerId === hostUserId`, so
 * a stranger who merely knows the `gameId` is refused. Since the same identity created
 * the game (via {@link createGame}, which also sends the token), this succeeds for the
 * host and only the host.
 */
/**
 * Create a zero-commitment demo game (#246, FR-17): a per-visitor, account-free
 * DYNAMIC + AUTO game on 3 curated topics. The server mints the visitor's guest
 * identity + pre-seeds the topics, so the caller just joins with the returned
 * `guestToken` and sends `start_generation` for an instant, zero-LLM board. Throws a
 * friendly message on the abuse limits (`429` rate / `503` global cap) or bad topics.
 */
export async function createDemoGame(topics: string[]): Promise<{
  gameId: string;
  joinCode: string;
  guestToken: string;
  playerId: string;
  topics: string[];
}> {
  const res = await fetch(`${SERVER_HTTP_URL}/api/demo/games`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    // Anonymous funnel key (#148 D148.4) so the server's `demo_started` log line joins
    // this demo to the pre-signup funnel head.
    body: JSON.stringify({ topics, fkey: getFunnelKey() }),
  });
  if (!res.ok) {
    if (res.status === 429 || res.status === 503) {
      throw new Error('Demos are busy right now — try again in a moment.');
    }
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Could not start the demo (${res.status})`);
  }
  return (await res.json()) as {
    gameId: string;
    joinCode: string;
    guestToken: string;
    playerId: string;
    topics: string[];
  };
}

/**
 * Launch the pre-generated board behind a `/trivia/<slug>` page (#295) as an
 * account-free HUMAN-hosted PACK game born IN_ROUND with the FULL committed board. The
 * server resolves the slug → its committed {@link TriviaBoard}, mints the visitor a
 * guest identity AND a host ticket, places the game, and returns the handle to host it
 * — mirroring {@link createDemoGame}. The visitor becomes the host (shared screen);
 * others join by code as players. Throws a friendly message on the abuse limit (`429`),
 * an unknown/unpublished slug (`404`), or a server failure.
 *
 * Like the demo/pack endpoints, this only exists on the deployed control plane (the
 * local dev server has no trivia board store), so it is a prod-mode-only surface.
 */
export async function createTriviaGame(slug: string): Promise<{
  gameId: string;
  joinCode: string;
  guestToken: string;
  hostTicket: string;
  playerId: string;
  boardId: string;
  slug: string;
}> {
  const res = await fetch(`${SERVER_HTTP_URL}/api/trivia/games`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    // Anonymous funnel key (#148 D148.4) so the server's `game_created` log line joins
    // this launch to the pre-signup funnel head.
    body: JSON.stringify({ slug, fkey: getFunnelKey() }),
  });
  if (!res.ok) {
    if (res.status === 429) {
      throw new Error('Too many game launches right now — try again in a moment.');
    }
    if (res.status === 404) {
      throw new Error('That trivia set is not available right now.');
    }
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Could not start the game (${res.status})`);
  }
  return (await res.json()) as {
    gameId: string;
    joinCode: string;
    guestToken: string;
    hostTicket: string;
    playerId: string;
    boardId: string;
    slug: string;
  };
}

export async function requestHostTicketForGame(gameId: string): Promise<string> {
  const guest = await ensureGuestIdentity();
  const res = await fetch(
    `${SERVER_HTTP_URL}/api/games/${encodeURIComponent(gameId)}/ticket`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ role: 'host', guestToken: guest.guestToken }),
    },
  );
  if (!res.ok) throw new Error(`Could not get host ticket (${res.status})`);
  const body = (await res.json()) as { ticket: string };
  return body.ticket;
}
